export type Role = {
  title: string;
  startDate: string;
  endDate: string | "Present";
  highlights?: string[];
};

export type Job = {
  title: string;
  company: string;
  companyUrl?: string;
  startDate: string;
  endDate: string | "Present";
  description?: string;
  highlights?: string[];
};

export type JobGroup = {
  company: string;
  companyUrl?: string;
  roles: Role[];
};

export type JobEntry = Job | JobGroup;

export const isJobGroup = (entry: JobEntry): entry is JobGroup => {
  return "roles" in entry;
};

export type Hackathon = {
  name: string;
  project: string;
  description: string;
  prize?: string;
  links?: { label: string; url: string }[];
};

export type Contribution = {
  repo: string;
  repoUrl?: string;
  description: string;
  prUrl?: string;
};

export type Project = {
  name: string;
  description: string;
  image?: string;
  url?: string;
  tags?: string[];
};

export type SocialLink = {
  label: string;
  url: string;
  icon?: string;
};

export const jobs: JobEntry[] = [
  {
    title: "Blockchain Lead",
    company: "Truststamp",
    companyUrl: "https://truststamp.ai/",
    startDate: "Nov 2025",
    endDate: "Present",
    highlights: [
      "Designed cross-chain non-custodial wallet on ICP using Rust",
      "Researched MPC platforms and distributed data storages for sharded data storage",
      "Advised on architectural decisions for blockchain infrastructure",
    ],
  },
  {
    company: "Cookie3",
    companyUrl: "https://www.cookie3.com/",
    roles: [
      {
        title: "Maintainer",
        startDate: "Jun 2025",
        endDate: "Present",
        highlights: [
          "Ongoing maintenance of deployed contracts",
          "Technological support for the team",
        ],
      },
      {
        title: "Core Blockchain Engineer",
        startDate: "Mar 2024",
        endDate: "Jun 2025",
        highlights: [
          "Launched the omnichain token, reaching an ATH market cap of $230M",
          "Coordinated the token launch and multiple exchange listings end-to-end",
          "Owned infra for safe wallets, lock-ups, vesting, and staking contracts",
          "Coordinated third-party audits securing $10M+ in smart contracts",
          "Built custom multicall contracts, reducing infra load by 50x",
        ],
      },
    ],
  },
  {
    title: "Core Blockchain Engineer",
    company: "Dayhub",
    companyUrl: "https://www.dayhub.io/",
    startDate: "Aug 2024",
    endDate: "Nov 2025",
    highlights: [
      "Architected a modular trading-challenge protocol using EIP-2535",
      "Integrated multiple oracles: Pyth, Chainlink, Uniswap, and RedStone",
      "Implemented ERC-7412 to securely bring off-chain data on-chain",
      "Enhanced UX by adopting ERC-7702 delegation for gas-less interactions",
      "Deployed an omnichain token via LayerZero",
    ],
  },
  {
    title: "Backend Web3 Developer",
    company: "FluidAI",
    startDate: "Oct 2023",
    endDate: "Feb 2024",
    highlights: [
      "Developed, tested, and debugged smart contracts for DEX use cases",
      "Built backend services for DEX aggregation using Nest.js",
      "Collaborated with the AI team to improve price prediction efficiency by 3%",
    ],
  },
  {
    title: "Solidity Engineer",
    company: "10Clouds",
    companyUrl: "https://10clouds.com/",
    startDate: "May 2022",
    endDate: "Sep 2023",
    highlights: [
      "Delivered DeFi and legal-tech smart contracts and DApps",
      "Conducted code reviews and security research, identifying 4 critical vulnerabilities",
      "Co-authored comprehensive smart contract audits",
    ],
  },
  {
    title: "Blockchain Developer",
    company: "Nextrope",
    companyUrl: "https://nextrope.com/",
    startDate: "Jan 2022",
    endDate: "May 2022",
    highlights: [
      "Integrated Robovault delta-neutral strategies after R&D on emerging DeFi products",
      "Implemented cross-chain features to enhance interoperability",
      "Led internal Web3 workshops for the engineering team",
    ],
  },
  {
    title: "Blockchain Developer",
    company: "Tubbly",
    companyUrl: "http://tubbly.io/",
    startDate: "Sep 2021",
    endDate: "Nov 2022",
    highlights: [
      "Developed and maintained smart contracts for an app rewarding physical activity",
    ],
  },
  {
    title: "Data Engineer",
    company: "Coinfirm",
    companyUrl: "https://coinfirm.com/",
    startDate: "Dec 2019",
    endDate: "Jan 2022",
    highlights: [
      "Aggregated and cleaned data from 15+ sources (blockchains, financial institutions, APIs)",
      "Shipped geolocation for 2M crypto addresses, expanding product capabilities",
      "Automated analytics, saving 400 hours/month ($48k/year)",
    ],
  },
];

export const hackathons: Hackathon[] = [
  {
    name: "Chainlink Fall 2022 Hackathon",
    project: "deBetting",
    description: "Decentralized betting platform",
    prize: "Winner",
    links: [
      { label: "deBetting", url: "https://devpost.com/software/debet-kyacl3" },
    ],
  },
  {
    name: "Chainlink Spring 2022 Hackathon",
    project: "Web3 Reputation Oracle",
    description:
      "Offchain computation of web3 identity for anti-sybil defense.",
    prize: "Winner",
    links: [
      {
        label: "Web3 Reputation Oracle",
        url: "https://devpost.com/software/web3-reputation-oracle",
      },
    ],
  },
  {
    name: "Solana Warsaw Hackathon 2026",
    project: "AnonMarkets",
    description: "Anonymous prediction market platform built on Solana",
    prize: "2nd Place",
  },
];

export const projects: Project[] = [
  {
    name: "VitaDAO",
    description: "DeFi protocol for longevity research funding",
    image: "/img/vitadao.png",
    url: "https://www.vitadao.com/",
    tags: ["DeSci", "DAO"],
  },
  {
    name: "Justice Technology",
    description: "Legal-tech platform with document tokenization",
    image: "/img/justice-tech.svg",
    url: "https://www.justicetech.io/",
    tags: ["NFTs", "Hyperledger"],
  },
  {
    name: "Zexus",
    description: "Decentralized lending marketplace for NFTs",
    image: "/img/zexus.png",
    url: "https://twitter.com/zexusfinance",
    tags: ["DeFi", "NFTs", "Lending", "Marketplace"],
  },
  {
    name: "HoneyFun",
    description: "NFT launchpad and marketplace built on Berachain ecosystem.",
    image: "/img/honeyfun.png",
    url: "https://honey.fun/",
    tags: ["NFT", "Launchpad"],
  },
  {
    name: "AI Agent Layer",
    description:
      "Infrastructure layer for deploying autonomous AI agents on-chain.",
    image: "/img/aiagentlayer.avif",
    url: "https://aiagentlayer.com/",
    tags: ["AI", "Infrastructure"],
  },
];

export const contributions: Contribution[] = [
  {
    repo: "OpenBlock",
    repoUrl: "https://openblock.com/",
    description:
      "Developed liquidity data aggregation tools for SpartaDEX on Linea. Built on-chain data indexing and analytics infrastructure.",
  },
  {
    repo: "PossumLabs",
    repoUrl: "https://www.possumlabs.io/",
    description:
      "Implemented arbitrage bot for liquidity pool rebalancing. Optimized trading strategies for DeFi protocols.",
  },
];

export const socialLinks: SocialLink[] = [
  { label: "GitHub", url: "https://github.com/spaceh3ad" },
  { label: "Twitter", url: "https://twitter.com/hussar_eth" },
  { label: "LinkedIn", url: "https://linkedin.com/in/jan-kwiatkowski-web3" },
  { label: "Email", url: "mailto:jan.kwiatkowski.web3@gmail.com" },
];

export const profile = {
  name: "Jan Kwiatkowski",
  bio: "Blockchain engineer with 6+ years of experience shipping production blockchain protocols.",
};

export const technologies = ["Solidity", "Rust", "TypeScript", "Python"];
